import * as React from "react";
import { Field } from "react-final-form";
import { FieldArrayProps } from "react-final-form-arrays";
import { useSelector } from "react-redux";

import { Close } from "@livechat/design-system-icons";
import { Button, FormField, IPickerListItem, Icon, Input, Picker } from "@livechat/design-system-react-components";

import { IAgentFormRow } from "components/agents-limit-reached";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { TeamPickerListItem, TeamPickerSelectedListItem } from "components/picker-items/teamPickerItems";
import { AgentRoleTooltip } from "components/tooltip";
import Role from "constants/role";
import { selectAgentsAlphabetically, selectIsAdmin } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { compose, emailOrEmpty, uniqueValue } from "utils/validators";

import { FormRow, StyledLabel } from "./styled";

interface IProps {
  index: number;
  hasTeams: boolean;
  fields: FieldArrayProps<IAgentFormRow, HTMLElement>;
  fieldName: string;
  teams: ITeam[];
}

const getTeamPickerListItem = (team: ITeam): IPickerListItem => ({
  key: team.id,
  name: team.name,
  customElement: {
    listItemBody: <TeamPickerListItem name={team.name} pickerType="multi" isDefault={team.isDefault} />,
    selectedItemBody: <TeamPickerSelectedListItem name={team.name} pickerType="multi" avatarOnly />,
  },
});

export const AgentFieldRow: React.FC<IProps> = ({ index, fields, hasTeams, fieldName, teams }) => {
  const isOwner = useSelector(selectIsAdmin);

  const alreadyRegisteredEmails = useSelector(selectAgentsAlphabetically).map(agent => agent.email);
  const permissionOptions = [
    ...(isOwner ? [{ key: Role.Owner, props: { id: Role.Owner, name: "Admin" } }] : []),
    { key: Role.Normal, props: { id: Role.Normal, name: "Agent" } },
    { key: Role.Viewer, props: { id: Role.Viewer, name: "Viewer" } },
  ];

  return (
    <FormRow data-testid="invite-agent-row">
      <Flex column width="100%">
        <Field<string>
          id={`email-${index}`}
          name={`${fieldName}[email]`}
          validate={compose(emailOrEmpty(), uniqueValue("Already registered in HelpDesk", alreadyRegisteredEmails))}
          autoFocus
          render={({ input, meta }) => (
            <>
              {index === 0 && <StyledLabel htmlFor="email-0">Email</StyledLabel>}
              <FormField error={meta.touched && meta.error}>
                <Input
                  {...input}
                  placeholder="Email"
                  type="email"
                  error={meta.error && meta.touched}
                  css={{ minWidth: "240px" }}
                  data-1p-ignore
                  autoComplete="off"
                  data-testid={`email-${index}`}
                />
              </FormField>
            </>
          )}
        />
      </Flex>
      <Block>
        {index === 0 && (
          <Flex inline alignItems="center">
            <StyledLabel>Role</StyledLabel>
            <AgentRoleTooltip />
          </Flex>
        )}
        <Field
          name={`${fieldName}[permission]`}
          render={({ input }) => (
            <Picker
              data-testid="role-dropdown"
              hideClearButton
              searchDisabled
              options={[
                ...(isOwner ? [{ key: Role.Owner, name: "Admin" }] : []),
                { key: Role.Normal, name: "Agent" },
                { key: Role.Viewer, name: "Viewer" },
              ]}
              selected={
                input.value
                  ? [
                      {
                        key: input.value,
                        name: permissionOptions.find(option => option.key === input.value)?.props.name || "",
                      },
                    ]
                  : []
              }
              onSelect={(selected: IPickerListItem[] | null) => {
                if (selected) {
                  input.onChange(selected[0].key);
                }
              }}
              css={{
                maxWidth: "100%",
                width: "120px",
              }}
            />
          )}
        />
      </Block>
      {hasTeams && isOwner && (
        <Block>
          {index === 0 && <StyledLabel>Team</StyledLabel>}
          <Field
            name={`${fieldName}[teams]`}
            validate={(value: string[]) => {
              if (!value || value.length === 0) {
                return "Select at least one team";
              }
            }}
          >
            {({ input, meta }) => {
              const selectedOptions = teams.filter(team => input.value.includes(team.id));

              const handleSelect = (selectedTeams: IPickerListItem[] | null): void => {
                if (selectedTeams) {
                  input.onChange(selectedTeams.map(team => team.key));
                } else {
                  input.onChange([]);
                }
              };

              return (
                <FormField error={meta.error}>
                  <Picker
                    data-testid={`team-dropdown-${index}`}
                    selectAllOptionText="Select all"
                    placeholder="Search..."
                    type="multi"
                    error={meta.error}
                    selected={selectedOptions.map(getTeamPickerListItem)}
                    options={teams.map(getTeamPickerListItem)}
                    onSelect={handleSelect}
                    css={{ width: "250px", maxWidth: "100%" }}
                  />
                </FormField>
              );
            }}
          </Field>
        </Block>
      )}
      {(fields.length ?? 0) > 1 && (
        <Block>
          {index === 0 && <StyledLabel>&nbsp;</StyledLabel>}
          <FormField>
            <Flex alignItems="center" height="36px">
              <Button
                size="xcompact"
                kind="text"
                onClick={() => fields.remove(index)}
                icon={<Icon source={Close} />}
                data-testid={`remove-agent-row-${index}`}
              />
            </Flex>
          </FormField>
        </Block>
      )}
    </FormRow>
  );
};
